/**
 * Le souvenir de l'invitation à créer un compte : un compteur et une date.
 *
 * # Pourquoi ce n'est pas une occasion unique
 *
 * La capsule de retour (`practice-feedback-prompt/prompt-storage`) pose une
 * clé définitive, parce qu'un avis ne se redemande pas : la personne l'a donné
 * ou elle a dit non, et dans les deux cas la question est close.
 *
 * Ici c'est l'inverse. Ce qu'on annonce — « ta progression ne vit que dans ce
 * navigateur » — devient *plus vrai* avec le temps : au premier refus la
 * personne n'a presque rien à perdre, trois semaines plus tard elle a un
 * morceau à moitié appris. Fermer la porte pour toujours au premier non,
 * c'est se priver du seul moment où l'argument porte vraiment.
 *
 * # Pourquoi un plafond quand même
 *
 * Une invitation répétable sans borne est du harcèlement avec un compteur.
 * Trois occasions espacées d'une semaine étalent la demande sur un mois, ce
 * qui couvre la période où la progression s'accumule ; la quatrième ne
 * dirait rien de neuf, elle dirait seulement qu'on n'écoute pas.
 *
 * # Pourquoi l'échec se referme
 *
 * Même raison que le voisin, en plus forte : c'est le compteur lui-même qui
 * porte la retenue. Un stockage qui ne sait ni lire ni écrire ne sait pas non
 * plus s'arrêter à trois, donc l'invitation reviendrait à chaque session
 * indéfiniment. Un stockage indisponible ou illisible se lit comme
 * « plafond atteint ».
 */

const STORAGE_KEY = "virtuosa.account.prompt";

/** Trois occasions au total, cf. l'en-tête. */
export const MAX_INVITATIONS = 3;

/**
 * Une semaine entre deux invitations.
 *
 * En dessous, la personne se souvient encore d'avoir dit non et la deuxième
 * demande annule la première. Au-dessus, elle a eu le temps de revenir, de
 * valider des phrases, et donc d'accumuler quelque chose qu'elle perdrait en
 * changeant d'écran — c'est ce quelque chose qui fait l'argument.
 */
export const MIN_GAP_MS = 7 * 24 * 60 * 60 * 1000;

type StoredAccountPrompt = {
  readonly v: 1;
  /** Nombre d'invitations déjà affichées sur ce navigateur. */
  readonly count: number;
  /** Date de la dernière, en millisecondes epoch. */
  readonly lastAt: number;
};

/**
 * Ce que le stockage contient, ou `null` s'il ne contient rien d'exploitable.
 *
 * `undefined` distingue le troisième cas : le stockage n'a pas pu être lu du
 * tout, ce qui n'est pas la même chose qu'un navigateur vierge.
 */
function read(): StoredAccountPrompt | null | undefined {
  if (typeof localStorage === "undefined") {
    return undefined;
  }

  let raw: string | null;

  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }

  if (raw === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (
      typeof parsed !== "object"
      || parsed === null
      || typeof (parsed as StoredAccountPrompt).count !== "number"
      || typeof (parsed as StoredAccountPrompt).lastAt !== "number"
    ) {
      return undefined;
    }

    return parsed as StoredAccountPrompt;
  } catch {
    // Valeur corrompue par une version antérieure ou par une autre main. On
    // la traite comme un stockage muet plutôt que comme un navigateur vierge :
    // repartir de zéro sur une clé illisible rouvrirait le compteur à chaque
    // chargement.
    return undefined;
  }
}

/**
 * Reste-t-il une occasion d'inviter, et l'écart est-il tenu ?
 *
 * `now` est injecté plutôt que lu ici pour la raison qui vaut dans tout le
 * parcours : une horloge passée en paramètre se teste, `Date.now()` se gèle.
 */
export function canInviteToAccount(now: number): boolean {
  const stored = read();

  if (stored === undefined) {
    return false;
  }

  if (stored === null) {
    return true;
  }

  if (stored.count >= MAX_INVITATIONS) {
    return false;
  }

  // Une horloge reculée donne un écart négatif, donc « trop tôt ». C'est le
  // bon sens de la garde : on ne rouvre pas le compteur parce que la machine
  // a changé d'heure.
  return now - stored.lastAt >= MIN_GAP_MS;
}

/**
 * Consigne une invitation affichée.
 *
 * Écrit à l'affichage et non à la réponse, comme le voisin : le sort le plus
 * probable n'est ni « elle accepte » ni « elle refuse », c'est « elle ferme
 * l'onglet ». Une invitation vue est une invitation dépensée.
 *
 * Silencieux en cas d'échec : {@link canInviteToAccount} refermera de toute
 * façon la porte au prochain chargement, puisqu'un stockage muet compte comme
 * plafond atteint.
 */
export function recordAccountInvitation(now: number): void {
  const stored = read();
  const count = (stored?.count ?? 0) + 1;
  const payload: StoredAccountPrompt = { v: 1, count, lastAt: now };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Stockage plein, refusé, ou navigation privée : rien à rattraper ici.
  }
}
